import PaginationParams, { ChannelParams } from "@/types/PaginationParams.interface"
import filterObjects, { FilterObject } from "@/utility/filter-objects"
import { store } from "@/store"

export { }

declare global {
    interface Array<T> {
        appSyncFilter(this: Array<any>, params: PaginationParams | ChannelParams, module: string)
        remove: (this: Array<any>, item: any) => void
        removeDuplicatesById: (this: Array<object>, arr: Array<object>) => Array<object>
        removeDuplicatesByIdUnshift: (this: Array<object>, arr: Array<object>) => Array<object>
        search: (this: Array<any>, txt: string, props: string[]) => Array<any>
    }
    interface String {
        copytoClipboard: (this: string) => void
        isNullOrWhiteSpace: (this: string) => boolean
        toKebabCase: (this: string) => string
        toProperCase: (this: string) => string
    }
}

/**
 *  Apply inline filtering for app sync changes using filter objects
 *  and the currently applied parameters for this kind of object.
 * 
 *  @method appSyncFilter()
 *  @param {PaginationParams} params - The current parameters applied in this module
 *  @return {any[]} - Our new filtered array
 */
Array.prototype.appSyncFilter = function (params: PaginationParams | ChannelParams, module: string) {
    //  Get our filters based on the module
    const filters = filterObjects[module]

    //  A small callback we can reuse for filtering
    const applyFilter = (funcs: object, val: any, filter: FilterObject, cond: boolean, x: any) => {
        //  Determine if this uses the default or not
        const useDefault = !funcs[val]

        //  Get the eval based on if we use the default or not
        const ev = useDefault ? filter.default(x, val) : funcs[val](x)

        //  Apply the filter to our item
        cond = cond && ev

        //  Return our condition
        return cond
    }

    //  Apply our filter to our array
    const filtered = this.filter(x => {
        //  Our condition to compare
        let cond = true

        //  Iterate through each filter we have
        filters.forEach((filter: FilterObject) => {
            //  Grab the key we want to compare for this filter
            const key = filter.key

            //  Grab the functions we use to actually filter
            const funcs = filter.functions

            //  Grab the val from our params so we can find which function to use
            const val = params[key]

            //  We convert our val into an array just for consistency since some might be an array
            const arrVal = Array.isArray(val) ? val : [val]

            //  Iterate through each value
            for (let i = 0; i < arrVal.length; i++) {
                //  Apply our filter for this specific value
                cond = applyFilter(funcs, arrVal[i], filter, cond, x)
            }
        })

        //  Return our condition
        return cond
    })

    //  Return our filtered array
    return filtered
}

/**
 * Remove an object from an array
 * 
 * @method  remove()
 * @param   {any} item - The item we want to remove from our array
 * @returns {array<T>} - Our new array
 */
Array.prototype.remove = function (item: any): void {
    //  Find the index of the item we're removing
    const i = this.indexOf(item)

    //  Remove the item from the selected tiles
    this.splice(i, 1)
}

/**
 *  Filters our array based on a search query and the properties passed in
 * 
 *  @method  search()
 *  @param   {string} txt - Our search query
 *  @param   {array<string>} props - An array containing all properties we want to search on
 *  @returns {array<any>} - The new filtered array
 */
Array.prototype.search = function (txt: string, props: string[]): Array<any> {
    //  Create our search lambda
    const search = (x: any, txt: string, props: string[]) => {
        if (!props.length) return false
        return x[props[0]].toLowerCase().includes(txt.toLowerCase()) || search(x, txt, props.slice(1))
    }

    //  Filter our array based on the search lambda
    return this.filter(x => {
        return search(x, txt, props)
    })
}

/**
 * Copies the specified string to the clipboard
 * 
 * @method  copytoClipboard()
 * @param   {string} this - Our string we are copying
 * @returns {void}
 */
String.prototype.copytoClipboard = function (): void {
    const el = document.createElement('textarea');
    el.value = this;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
}
/**
 * Checks if a string is null or contains whitespace
 * 
 * @method  isNullOrWhiteSpace()
 * @param   {string} this - Our string we are checking
 * @returns {boolean} - Evaluation of our statement
 */
String.prototype.isNullOrWhiteSpace = function (): boolean {
    return (!this || this.length === 0 || /^\s*$/.test(this));
};
/**
 * Returns string in Kebab Case  (ex: randomString -> random-string)
 * 
 * @method  toKebabCase()
 * @param   {string} this - Our stirng to convert
 * @returns {string} - Our kebab case string
 */
String.prototype.toKebabCase = function (): string {
    return this.replace(/([a-z])([A-Z])/g, '$1-$2').replace(/\s+/g, '-').toLowerCase();
}
/**
 * Returns our string in proper case (ex: randomString -> RandomString)
 * 
 * @method  toProperCase()
 * @param   {string} this - Our string to convert
 * @returns {string} - Our proper case string
 */
//  Returns our string in proper case
String.prototype.toProperCase = function (): string {
    //  We

    return this.replace(/_/g, ' ').replace(/\w\S*/g, function (txt: string) { return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(); });
}

/**
 * Because of how IE and Safari parse dates, we cannot use ISO 8601
 * in the new Date() function. This converts any ISO8601 dates into
 * a format that can be read by any browser
 * 
 * @param str - an iso-8601 date time string
 * @returns {Date} - The parsed date
 */
window.getDate = function (str: string): Date {
    //  If our value is null, return the default
    if (!str) return new Date('1969-12-31 16:00')

    //  Convert this to a date the easy way
    const easy = new Date(str)

    //  @ts-ignore This is considered valid IF it is a Date AND it is NOT NaN (not a number)
    const valid = easy instanceof Date && !isNaN(easy)

    //  If this is a valid date then just return it
    if (valid) return easy

    //  Else, we need to parse :-) (split into date and time by string)
    const [date, time] = str.split(" ")

    //  Grab the year, month, and day from the date
    const [year, month, day] = date.split("-")

    //  Create our date time string
    const dts = `${month}/${day}/${year} ${time}`

    //  Return the date
    return new Date(dts)
}

/**
 * Fetch the content of a text file stored online
 * 
 * @method  getFileContent()
 * @param   {string} url - the location of the file 
 * @returns {string} - The contents of our file
 */
window.getFileContent = async function (url: string): Promise<string> {
    try {
        //  Fetch the file
        const response = await fetch(url)

        //  Read the file body as a string
        const content = await response.text()

        //  Return the content
        return content
    } catch (e) {
        console.error(e)
    }
}
/**
 * Returns object values specified in props array. If not an object, returns str
 * 
 * @method  getProps()
 * @param   {object} obj  - The object to iterate through
 * @param   {Array<string>} props - An array of the nested properties you want to get to
 * @param   {string} str  - The string you want to return if the prop doesn't exist 
 * @returns {any} - The resulting value of our property
 */
window.getProps = function (obj: object, props: Array<string>, str: string = ''): any {
    if (obj == null || typeof obj !== 'object') return str
    if (props.length === 1) return obj[props[0]]
    return window.getProps(obj[props[0]], props.slice(1), str)
}
/**
 * Pops our intercom support messenger
 * 
 * @method  popSupportMessenger()
 * @returns {void}
 */
window.popSupportMessenger = async function (): Promise<void> {
    //  Check if we are on the mobile app
    const isApp = store.getters.isApp

    //  If we are, trigger the plugin
    //  @ts-ignore
    if (isApp) return intercom.displayMessenger()

    //  Get the 'help' button (hosted in Navigation Drawer, it is bound to intercom messenger)
    const btn = document.getElementById('messagedesk-help-button');

    //  Click the 'help' button to pop support messenger
    btn.click()
}
/**
 * Merges arrays and returns an array with unique values by id
 * 
 * @method  removeDuplicatesById()
 * @param   {Array<object>} arr1 - The first of 2 arrays we are merging
 * @returns {Array<object>} - Our merged array
 */
window.removeDuplicatesById = function (arr1: Array<object>, arr2: Array<object>, arr3: Array<object> = null): Array<any> {
    //  Create the array we will use to merge
    const preMerge = [...arr1, ...arr2]

    //  Difference the 3 arrays: only keep values that are in arr1 and arr2 but NOT in arr3
    const arrays = arr3 ? window._.differenceBy(preMerge, arr3, 'id') : preMerge

    // Merge the arrays
    const mergedArrays = window._.flatten(window._.reject(arrays, window._.isNil))

    // Create a map that stores the index of the first id
    const uniqueMap = new Map()

    //  Copy our array so that we don't end up modifying it in place
    const uniqueArr = []

    // Iterate over the merged arrays
    mergedArrays.forEach((obj, i: number) => {
        // If the map contains the key of the object id, then get the index from the map value and update the object in place
        if (uniqueMap.has(obj.id)) {
            //  Grab the index of our item to replace
            const idx = uniqueMap.get(obj.id)

            // Update the first index
            //  We use _.extend to copy properties from obj into our existing object so we can do sparse updates
            //  i.e. a = {id: 1, text: "hello", updated: 1}, b = {id: 1, updated: 2}, _.extend(a, b) = {id: 1, text: "hello", updated: 2}
            uniqueArr[idx] = window._.extend(uniqueArr[idx], obj)
        }
        // else, map the object id to the index in the array
        else {
            //  Add it to our uniqueMap so we can ensure uniqueness
            uniqueMap.set(obj.id, i)
            //  Add it to our array that we will return
            uniqueArr.push(obj)
        }
    })

    // Return unique array
    return uniqueArr
}
/**
 * Merges arrays and returns an array with unique values by id
 * 
 * @method  removeDuplicatesByIdUnshift()
 * @param   {Array<object>} arr1 - The first of 2 arrays we are merging
 * @returns {Array<object>} - Our merged array
 */
window.removeDuplicatesByIdUnshift = function (arr1: Array<object>, arr2: Array<object>, arr3: Array<object> = null): Array<any> {
    //  Create the array we will use to merge
    const preMerge = [...arr1, ...arr2]

    //  Difference the 3 arrays: only keep values that are in arr1 and arr2 but NOT in arr3
    const arrays = arr3 ? window._.differenceBy(preMerge, arr3, 'id') : preMerge

    // Merge the arrays
    const mergedArrays = window._.flatten(window._.reject(arrays, window._.isNil))

    // Create a map that stores the index of the first id
    const uniqueMap = new Map()

    //  Copy our array so that we don't end up modifying it in place
    const uniqueArr = []

    // Iterate over the merged arrays
    mergedArrays.forEach((obj, i: number) => {
        // If the map contains the key of the object id, then get the index from the map value and update the object in place
        if (uniqueMap.has(obj.id)) {
            //  Grab the index of our item to replace
            const idx = uniqueMap.get(obj.id)

            // Update the first index
            //  We use _.extend to copy properties from obj into our existing object so we can do sparse updates
            //  i.e. a = {id: 1, text: "hello", updated: 1}, b = {id: 1, updated: 2}, _.extend(a, b) = {id: 1, text: "hello", updated: 2}
            uniqueArr[idx] = window._.extend(uniqueArr[idx], obj)
        }
        // else, map the object id to the index in the array
        else {
            //  Add it to our uniqueMap so we can ensure uniqueness
            uniqueMap.set(obj.id, i)
            //  Add it to our array that we will return
            uniqueArr.unshift(obj)
        }
    })

    // Return unique array
    return uniqueArr
}
/**
 * Convert an object of key => value to url query parameter string
 * 
 * @method  toUrlParams()
 * @param   {object} obj - Our object we are converting 
 * @returns {string} - Our url params
 */
window.toUrlParams = function (obj: any): string {
    //  Return empty string if the input is null
    if (!obj) return ''

    //  Remove null values
    Object.keys(obj).forEach(key => {
        if (obj[key] === null || obj[key] === undefined) delete obj[key];
    });

    //  Parse object into url parameters
    return new URLSearchParams(obj).toString()
}